/**
 * 认证 API
 * @module core/api/auth
 *
 * 所有公共参数（device_id, mac_code, device_code, ekey, source, soft_id 等）
 * 以及推广参数（mp, bdVid, qhclickid, strategy, invite_id）都由拦截器自动添加
 */

import type { HttpClient } from '../http/HttpClient'
import type {
  LoginParams,
  WechatLoginParams,
  MiniProgramLoginParams,
  LoginResponse,
  AuthSession,
  QRCodeResponse,
  LoginStatusResponse,
  CaptchaInfo,
  GetSlideCaptchaParams,
  SendVerifyCodeWithCaptchaParams,
  SendVerifyCodeWithSlideParams,
  SendVerifyCodeResponse,
  SlideCaptchaInfo,
  UserInfo,
  CreateSsoCodeParams,
  SsoCodeResponse,
  RefreshSessionParams
} from '../../types/auth'
import type { APIResponse } from '../../types/api'
import { API_ENDPOINTS } from '../../constants/api-endpoints'
import { QNW_SSO_ERROR_CODES, QNW_SSO_ERROR_MESSAGES } from '../../constants/errors'
import { CsdError } from '../../types/error'
import { getApplet, getSoftId, getWxLoginAndPayDomain } from '../config/SDKConfigManager'
import { stringifyQueryParams } from '../utils/url'

function isSsoCodeResponse(data: unknown): data is SsoCodeResponse {
  if (!data || typeof data !== 'object') {
    return false
  }

  const value = data as Record<string, unknown>
  return (
    typeof value.sso_code === 'string' &&
    typeof value.expires_in === 'number' &&
    typeof value.redirect_url === 'string'
  )
}

function normalizeLoginResponseData(data: LoginResponse): LoginResponse {
  return {
    ...data,
    userInfo: data.userInfo || ({} as UserInfo),
    expire_at: data.expire_at || Date.now() + 7 * 24 * 60 * 60 * 1000
  }
}

function unwrapApiData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export class AuthAPI {
  private http: HttpClient

  constructor(http: HttpClient) {
    this.http = http
  }

  /** 获取登录二维码 */
  async getLoginQRCode(): Promise<QRCodeResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.get<unknown>(
      API_ENDPOINTS.AUTH.GET_QR_CODE,
      loginBase ? { baseURL: loginBase } : undefined
    )

    const responseData = response.data as {
      code: number
      msg?: string
      data?: { url: string; content?: string; ewm_id: string }
    }

    if (responseData.code === 0 && responseData.data) {
      const data = responseData.data
      return {
        url: data.url,
        content: data.content || data.url,
        ewm_id: data.ewm_id,
        expire_at: Date.now() + 300000,
        expiresIn: 300
      }
    }

    throw new Error(responseData.msg || '获取二维码失败')
  }

  /**
   * 检查登录状态
   * @param ewmId - 二维码 ID
   */
  async checkLoginStatus(ewmId: string): Promise<LoginStatusResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.get<unknown>(
      `${API_ENDPOINTS.AUTH.CHECK_LOGIN}?temporary_id=${encodeURIComponent(ewmId)}`,
      loginBase ? { baseURL: loginBase } : undefined
    )

    const responseData = response.data as {
      code: number
      data?: LoginResponse
    }

    if (responseData.code === 0 && responseData.data?.token) {
      return {
        status: 'confirmed',
        ...normalizeLoginResponseData(responseData.data)
      }
    } else if (responseData.code >= 300) {
      return { status: 'pending' }
    }

    return { status: 'pending' }
  }

  /**
   * 发送验证码
   * @param phone - 手机号
   */
  async sendVerifyCode(phone: string): Promise<void> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.get<unknown>(
      `${API_ENDPOINTS.AUTH.SEND_VERIFY_CODE}?phone=${encodeURIComponent(phone)}&t=${Date.now()}`,
      loginBase ? { baseURL: loginBase } : undefined
    )

    const responseData = response.data as { code: number; msg?: string }
    if (responseData.code >= 300) {
      throw new Error(responseData.msg || '发送验证码失败')
    }
  }

  /**
   * 手机号登录
   * @param params - 登录参数（phone, code）
   */
  async phoneLogin(params: LoginParams): Promise<LoginResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.get<unknown>(
      `${API_ENDPOINTS.AUTH.PHONE_LOGIN}?phone=${encodeURIComponent(params.phone)}&code=${encodeURIComponent(params.code)}`,
      loginBase ? { baseURL: loginBase } : undefined
    )

    const responseData = response.data as {
      code: number
      msg?: string
      data?: LoginResponse
    }

    if (responseData.code === 0 && responseData.data?.token) {
      return normalizeLoginResponseData(responseData.data)
    }

    throw new Error(responseData.msg || '登录失败')
  }

  /** 微信登录 */
  async wechatLogin(params: WechatLoginParams): Promise<LoginResponse> {
    const response = await this.http.post<APIResponse<LoginResponse>>(
      API_ENDPOINTS.AUTH.WECHAT_LOGIN,
      params
    )
    return response.data.data
  }

  /** 小程序登录 */
  async miniProgramLogin(params: MiniProgramLoginParams): Promise<LoginResponse> {
    const response = await this.http.post<APIResponse<LoginResponse>>(
      API_ENDPOINTS.AUTH.MINI_PROGRAM_LOGIN,
      params
    )
    return response.data.data
  }

  /** 创建 SSO code */
  async createSsoCode(params: CreateSsoCodeParams): Promise<SsoCodeResponse> {
    const response = await this.http.post<unknown>(
      API_ENDPOINTS.AUTH.CREATE_SSO_CODE,
      stringifyQueryParams({
        source_soft_id: params.source_soft_id || getSoftId(),
        target_soft_id: params.target_soft_id,
        redirect_uri: params.redirect_uri
      }),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }
    )

    const responseData = response.data as {
      code: number
      msg?: string
      data?: unknown
    }

    if (!responseData || typeof responseData.code !== 'number') {
      throw new CsdError(
        QNW_SSO_ERROR_MESSAGES[QNW_SSO_ERROR_CODES.RESPONSE_INVALID],
        QNW_SSO_ERROR_CODES.RESPONSE_INVALID,
        response
      )
    }

    if (responseData.code !== 0) {
      const message =
        responseData.msg || QNW_SSO_ERROR_MESSAGES[responseData.code] || 'SSO 鉴权失败'
      throw new CsdError(message, responseData.code, response)
    }

    if (!isSsoCodeResponse(responseData.data)) {
      throw new CsdError(
        QNW_SSO_ERROR_MESSAGES[QNW_SSO_ERROR_CODES.RESPONSE_INVALID],
        QNW_SSO_ERROR_CODES.RESPONSE_INVALID,
        response
      )
    }

    return responseData.data
  }

  /** 旧版刷新 Token：历史兼容，不用于标准双 token 机制 */
  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.post<APIResponse<LoginResponse>>(
      API_ENDPOINTS.AUTH.REFRESH_TOKEN,
      { refreshToken },
      {
        ...(loginBase ? { baseURL: loginBase } : {}),
        headers: {
          Authorization: refreshToken
        }
      }
    )
    return response.data.data
  }

  /** 标准双 token 刷新会话 */
  async refreshSession(params: RefreshSessionParams): Promise<AuthSession> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.post<APIResponse<AuthSession>>(
      API_ENDPOINTS.AUTH.REFRESH_SESSION,
      params,
      {
        ...(loginBase ? { baseURL: loginBase } : {}),
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    return response.data.data
  }

  /** 获取图片验证码 */
  async getImageCaptcha(): Promise<CaptchaInfo> {
    const response = await this.http.get<APIResponse<CaptchaInfo>>(API_ENDPOINTS.AUTH.GET_CAPTCHA)
    return response.data.data
  }

  /** 获取滑块验证码 */
  async getSlideCaptcha(params: GetSlideCaptchaParams = {}): Promise<SlideCaptchaInfo> {
    const query = stringifyQueryParams({
      soft_id: getSoftId(),
      phone: params.phone,
      sms_type: params.smsType || 'login',
      t: Date.now()
    })
    const response = await this.http.get<APIResponse<SlideCaptchaInfo>>(
      `${API_ENDPOINTS.AUTH.GET_SLIDE_CAPTCHA}?${query}`
    )
    return unwrapApiData<SlideCaptchaInfo>(response.data)
  }

  /** 发送验证码（带图片验证码） */
  async sendVerifyCodeWithCaptcha(
    params: SendVerifyCodeWithCaptchaParams
  ): Promise<SendVerifyCodeResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const query = stringifyQueryParams({
      phone: params.phone,
      captcha_key: params.captcha_key,
      captcha_code: params.captcha_code,
      smsType: params.smsType,
      t: Date.now()
    })
    const response = await this.http.get<unknown>(
      `${API_ENDPOINTS.AUTH.SEND_VERIFY_CODE}?${query}`,
      loginBase ? { baseURL: loginBase } : undefined
    )
    const responseData = response.data as SendVerifyCodeResponse
    if (responseData.code >= 300) {
      throw new Error(responseData.msg || '发送验证码失败')
    }
    return responseData
  }

  /** 发送验证码（带滑块验证） */
  async sendVerifyCodeWithSlide(
    params: SendVerifyCodeWithSlideParams
  ): Promise<SendVerifyCodeResponse> {
    const response = await this.http.post<
      APIResponse<SendVerifyCodeResponse> | SendVerifyCodeResponse
    >(
      API_ENDPOINTS.AUTH.SEND_VERIFY_CODE_WITH_SLIDE,
      {
        phone: params.phone,
        captcha_key: params.captcha_key,
        slide_x: params.slide_x,
        slide_y: params.slide_y,
        sms_type: params.smsType,
        soft_id: getSoftId()
      }
    )
    const responseData = response.data as APIResponse<SendVerifyCodeResponse> | SendVerifyCodeResponse
    if ('code' in responseData && responseData.code >= 300) {
      throw new Error(responseData.msg || '发送验证码失败')
    }
    return unwrapApiData<SendVerifyCodeResponse>(responseData) || { code: 0, msg: 'success' }
  }

  /** 获取小程序登录二维码 */
  async getMiniProgramLoginQrcode(params: {
    env_version?: 'release' | 'trial' | 'devop'
    login_params?: string
  }): Promise<{ base64_img: string; uuid: string }> {
    const applet = getApplet()
    const query = stringifyQueryParams({
      ...(applet ? { applet } : {}),
      ...(params.env_version ? { env_version: params.env_version } : {}),
      ...(params.login_params ? { login_params: params.login_params } : {})
    })
    const url = query
      ? `${API_ENDPOINTS.AUTH.MINI_PROGRAM_LOGIN_QR}?${query}`
      : API_ENDPOINTS.AUTH.MINI_PROGRAM_LOGIN_QR
    const response = await this.http.get<APIResponse<{ base64_img: string; uuid: string }>>(url)
    return response.data.data
  }

  /** 获取小程序登录 Token */
  async getMiniProgramLoginTokenByQRCode(login_uuid: string): Promise<LoginResponse | { token?: string }> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.get<APIResponse<LoginResponse | { token?: string }>>(
      `${API_ENDPOINTS.AUTH.MINI_PROGRAM_GET_TOKEN_BY_QR}?login_uuid=${encodeURIComponent(
        login_uuid
      )}`,
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }

  /** 获取小程序登录参数 */
  async getMiniProgramLoginParamsByUUID(login_uuid: string): Promise<any> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.get<APIResponse<any>>(
      `${API_ENDPOINTS.AUTH.MINI_PROGRAM_GET_LOGIN_PARAMS}?login_uuid=${encodeURIComponent(
        login_uuid
      )}`,
      loginBase ? { baseURL: loginBase } : undefined
    )
    return response.data.data
  }

  /** 绑定小程序登录 Token */
  async bindMiniProgramTokenByUUID(login_uuid: string): Promise<void> {
    const loginBase = getWxLoginAndPayDomain()
    await this.http.post(
      `${API_ENDPOINTS.AUTH.MINI_PROGRAM_BIND_TOKEN_BY_UUID}?login_uuid=${encodeURIComponent(
        login_uuid
      )}`,
      undefined,
      loginBase ? { baseURL: loginBase } : undefined
    )
  }

  /** 小程序登录（旧版接口） */
  async miniProgramLoginLegacy(params: Record<string, unknown>): Promise<any> {
    const response = await this.http.post(API_ENDPOINTS.AUTH.MINI_PROGRAM_LOGIN_LEGACY, params)
    return response.data.data
  }

  /**
   * 设备号登录
   * 使用设备信息自动登录，无需用户交互
   * @param params - 设备公共参数（device_id, mac_code, device_code, soft_id 等）
   */
  async deviceLogin(params: Record<string, string>): Promise<LoginResponse> {
    const loginBase = getWxLoginAndPayDomain()
    const response = await this.http.post<unknown>(
      API_ENDPOINTS.AUTH.DEVICE_LOGIN,
      stringifyQueryParams(params),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        ...(loginBase ? { baseURL: loginBase } : {})
      }
    )

    const responseData = response.data as {
      code: number
      msg?: string
      data?: LoginResponse
    }

    if (responseData.code === 0 && responseData.data?.token) {
      return normalizeLoginResponseData(responseData.data)
    }

    throw new Error(responseData.msg || '设备号登录失败')
  }
}
